import {observable, transaction} from 'mobx';
import {LineChartPreviewInBlockProps} from '../views/PaperSheetBlocks/LineChartPreview';
import {LineChartData} from '../../views/charts/LineChart';
import _ from 'lodash';
import {getHourlyProductData, getHourlyProductDataSummary} from '../../apis/ProductAPI';
import {getHourlySearchtermData, getHourlySearchtermDataSummary} from '../../apis/SearchtermAPI';
import moment from 'moment';
import {Channel} from '../../types/Channel';
import {DateUtils} from '../../utility/DateUtils';
import {DateRange} from '../../stores/TimeIntervalStore';
import {AxiosResponse} from 'axios';
import {TimeSeriesEntry} from '../../apis/TimeSeriesEntry';
import {color, ColorHelper} from 'csx';
import {DIAGRAM_COLORS} from '../../utility/ColorUtils';
import {BlackColor} from '../../styles/colors';
import {VisibleAttribute} from '../../types/Chart';
import {DiagramInstruction} from '../../stores/OdtExportStore';

export interface LineChartPreviewState {
    dataLoading: boolean;
    data: LineChartData[];
    checkForChartUpdate: (newProps: LineChartPreviewInBlockProps, oldProps?: LineChartPreviewInBlockProps) => void;
    generateVisibleAttributes: (props: LineChartPreviewInBlockProps) => VisibleAttribute[];
}

interface LineChartDataMap {
    [timestamp: number]: LineChartData;
}

export default class LineChartPreviewStore implements LineChartPreviewState {
    @observable public dataLoading = false;
    @observable public data: LineChartData[] = [];

    constructor(private getFullChannelForChannelName: (channelName: string) => Channel) {}

    public generateVisibleAttributes(props: LineChartPreviewInBlockProps): VisibleAttribute[] {
        const {searchDiagrams, productDiagrams} = props.mergedBuildingBlockInstructions.diagramInstruction
            ? props.mergedBuildingBlockInstructions.diagramInstruction
            : {searchDiagrams: [], productDiagrams: []};

        const compareDiagram = props.compareDateRange ? [false, true] : [false];

        const flatMapVisibleAttributes = (flatAttributes: VisibleAttribute[], attributes: VisibleAttribute[]): VisibleAttribute[] => {
            return [...flatAttributes, ...attributes];
        };

        if (searchDiagrams.length > 0) {
            return compareDiagram
                .map((isCompareDiagram) => {
                    return searchDiagrams.map((searchDiagram, index) => ({
                        kpiRequestInfo: {
                            channel: props.getFullChannelForChannelName(searchDiagram.channel),
                            searchtermOrProductId: searchDiagram.searchterm ? searchDiagram.searchterm : searchDiagram.channel,
                            kpi: searchDiagram.kpi,
                            isChannelDiagram: false,
                            isCompareDiagram,
                        },
                        useAbsValue: true,
                        color: this.getDiagramColorByIndex(index),
                    }));
                })
                .reduce(flatMapVisibleAttributes, []);
        } else if (productDiagrams.length > 0) {
            return compareDiagram
                .map((isCompareDiagram) => {
                    return productDiagrams.map((productDiagram, index) => ({
                        kpiRequestInfo: {
                            channel: props.getFullChannelForChannelName(productDiagram.channel),
                            searchtermOrProductId: productDiagram.productId ? productDiagram.productId : productDiagram.channel,
                            kpi: productDiagram.kpi,
                            isChannelDiagram: false,
                            isCompareDiagram,
                        },
                        useAbsValue: true,
                        color: this.getDiagramColorByIndex(index),
                    }));
                })
                .reduce(flatMapVisibleAttributes, []);
        }
        return [];
    }

    public checkForChartUpdate(newProps: LineChartPreviewInBlockProps, oldProps?: LineChartPreviewInBlockProps) {
        if (!oldProps) {
            this.updateDiagramData(newProps);
            return;
        }
        const {diagramInstruction: newDiagramInstructions} = newProps.mergedBuildingBlockInstructions;
        const {diagramInstruction: oldDiagramInstructions} = oldProps.mergedBuildingBlockInstructions;

        if (newDiagramInstructions && oldDiagramInstructions) {
            if (
                !_.isEqual(newDiagramInstructions.searchDiagrams, oldDiagramInstructions.searchDiagrams) ||
                !_.isEqual(newDiagramInstructions.productDiagrams, oldDiagramInstructions.productDiagrams) ||
                !_.isEqual(newProps.dateRange, oldProps.dateRange) ||
                !_.isEqual(newProps.compareDateRange, oldProps.compareDateRange) ||
                newProps.targetCurrency !== oldProps.targetCurrency
            ) {
                this.updateDiagramData(newProps);
                return;
            }
        }
    }

    private generateChartPromise(props: LineChartPreviewInBlockProps): Promise<Array<AxiosResponse<TimeSeriesEntry[]>>> {
        const {productDiagrams, searchDiagrams} = props.mergedBuildingBlockInstructions.diagramInstruction
            ? props.mergedBuildingBlockInstructions.diagramInstruction
            : {searchDiagrams: [], productDiagrams: []};
        const dateRanges = [props.dateRange, props.compareDateRange].filter((dateRange) => dateRange !== undefined) as DateRange[];
        const flatMapPromises = (
            flatPromises: Array<Promise<AxiosResponse<TimeSeriesEntry[]>>>,
            promises: Array<Promise<AxiosResponse<TimeSeriesEntry[]>>>
        ) => {
            return [...flatPromises, ...promises];
        };

        return productDiagrams.length > 0
            ? Promise.all(
                  dateRanges
                      .map((dateRange) =>
                          productDiagrams.map((productDiagram) => {
                              if (productDiagram.productId) {
                                  return getHourlyProductData({
                                      channel: productDiagram.channel,
                                      interval: DateUtils.toHourlyDateRange(dateRange),
                                      kpi: productDiagram.kpi,
                                      productId: productDiagram.productId,
                                      targetCurrency: props.targetCurrency,
                                  });
                              }
                              return getHourlyProductDataSummary({
                                  channel: productDiagram.channel,
                                  interval: DateUtils.toHourlyDateRange(dateRange),
                                  kpi: productDiagram.kpi,
                                  targetCurrency: props.targetCurrency,
                              });
                          })
                      )
                      .reduce(flatMapPromises, [])
              )
            : Promise.all(
                  dateRanges
                      .map((dateRange) =>
                          searchDiagrams.map((searchDiagram) => {
                              if (searchDiagram.searchterm) {
                                  return getHourlySearchtermData({
                                      kpi: searchDiagram.kpi,
                                      interval: DateUtils.toHourlyDateRange(dateRange),
                                      channel: searchDiagram.channel,
                                      searchterm: searchDiagram.searchterm,
                                      targetCurrency: props.targetCurrency,
                                  });
                              }
                              return getHourlySearchtermDataSummary({
                                  kpi: searchDiagram.kpi,
                                  interval: DateUtils.toHourlyDateRange(dateRange),
                                  channel: searchDiagram.channel,
                                  targetCurrency: props.targetCurrency,
                              });
                          })
                      )
                      .reduce(flatMapPromises, [])
              );
    }

    private chartResponsesToLineChartList(
        chartResps: Array<AxiosResponse<TimeSeriesEntry[]>>,
        chartRequestData: DiagramInstruction[],
        compareActive: boolean
    ): LineChartData[] {
        return chartResps
            .map((resp, respIndex) => {
                return resp.data.map((dataPoint) => {
                    const getKpi = () => {
                        if (respIndex > chartRequestData.length - 1) {
                            return chartRequestData[respIndex - chartRequestData.length].kpi;
                        }
                        return chartRequestData[respIndex].kpi;
                    };
                    const isCompareDiagram = (): boolean => {
                        if (compareActive) {
                            return chartResps.length / 2 - 1 < respIndex;
                        }
                        return false;
                    };
                    return {
                        data: [
                            {
                                value: dataPoint.count,
                                relValue: dataPoint.relCount,
                                kpi: getKpi(),
                                isChannelDiagram: false,
                                isCompareDiagram: isCompareDiagram(),
                                searchtermOrProductId: dataPoint.primary,
                                channel: this.getFullChannelForChannelName(dataPoint.channel),
                            },
                        ],
                        date: moment(dataPoint.timestamp, 'x'),
                        timestamp: dataPoint.timestamp,
                    };
                });
            })
            .reduce((data: LineChartData[], linePoints: LineChartData[]) => [...data, ...linePoints], []);
    }

    private groupChartLinesByTimestamp(chartLines: LineChartData[]): LineChartData[] {
        const chartDataMap = chartLines.reduce((data: LineChartDataMap, line) => {
            if (line.timestamp) {
                if (data[line.timestamp]) {
                    data[line.timestamp] = {
                        date: data[line.timestamp].date,
                        data: [...data[line.timestamp].data, ...line.data],
                    };
                } else {
                    data[line.timestamp] = {
                        date: line.date,
                        data: line.data,
                    };
                }
            }
            return data;
        }, {});

        return Object.values(chartDataMap).sort((a: LineChartData, b: LineChartData) => (a.timestamp || 0) - (b.timestamp || 0)) as LineChartData[];
    }

    private updateDiagramData(props: LineChartPreviewInBlockProps) {
        const {productDiagrams, searchDiagrams} = props.mergedBuildingBlockInstructions.diagramInstruction
            ? props.mergedBuildingBlockInstructions.diagramInstruction
            : {productDiagrams: [], searchDiagrams: []};

        if (productDiagrams.length === 0 && searchDiagrams.length === 0) {
            transaction(() => {
                this.dataLoading = false;
                this.data = [];
            });
            return;
        }

        transaction(() => {
            this.dataLoading = true;
            this.data = [];
        });

        this.generateChartPromise(props)
            .then((chartResps) => {
                const chartRequestData = productDiagrams.length > 0 ? productDiagrams : searchDiagrams;
                const chartLines = this.chartResponsesToLineChartList(chartResps, chartRequestData, props.compareDateRange !== undefined);
                transaction(() => {
                    this.dataLoading = false;
                    this.data = this.groupChartLinesByTimestamp(chartLines);
                });
            })
            .catch(() => {
                transaction(() => {
                    this.dataLoading = false;
                    this.data = [];
                });
            });
    }

    private getDiagramColorByIndex(index: number): ColorHelper {
        return DIAGRAM_COLORS[index] ? DIAGRAM_COLORS[index] : color(BlackColor);
    }
}



// WEBPACK FOOTER //
// ./src/report/stores/LineChartPreviewStore.ts